import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0TagStatus } from '@factorialco/f0-react'
import { BillingAddonReferenceFragment, UpsellLandingPage } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { OneLandingButton } from 'modules/upsell/components/OneLanding/Button'
import { GradientMediaContainer } from 'modules/upsell/pages/OneLanding/_Sections/FeaturesSection'

interface OneLandingHeaderProps {
  content: UpsellLandingPage | undefined
  billingAddon: BillingAddonReferenceFragment | null
  moduleName: string
  alreadyInOneWaitlist: boolean
}

interface BlurEllipsisProps {
  top: string
  left: string
  width: string
  height: string
  blur: string
  color: string
}

const BlurEllipsis = ({ top, left, width, height, blur, color }: BlurEllipsisProps) => (
  <div
    style={{
      position: 'absolute',
      top,
      left,
      transform: 'translate(-50%, -50%)',
      width,
      height,
      background: color,
      borderRadius: '50%',
      filter: `blur(${blur})`,
      zIndex: -1,
    }}
  />
)

const ellipsisConfig: BlurEllipsisProps[] = [
  {
    top: '30%',
    left: '35%',
    width: '200px',
    height: '200px',
    blur: '90px',
    color: '#E51943',
  },
  {
    top: '60%',
    left: '8%',
    width: '200px',
    height: '200px',
    blur: '60px',
    color: '#A4A5DE',
  },
  {
    top: '60%',
    left: '90%',
    width: '200px',
    height: '200px',
    blur: '90px',
    color: '#F97316',
  },
]

const OneLandingHeader = ({
  content,
  billingAddon,
  moduleName,
  alreadyInOneWaitlist,
}: OneLandingHeaderProps) => {
  const videoId = content?.mediaUrl?.split('v=')[1]?.split('&')[0]

  return (
    <Box className="mx-auto" alignItems="center" textAlign="center" gap="s16">
      {content?.tagLabel && !alreadyInOneWaitlist && (
        <F0TagStatus text={content?.tagLabel} variant="neutral" />
      )}

      {alreadyInOneWaitlist && (
        <F0TagStatus
          text={content?.installedTagLabel || i18n.t('growth.one_waitlist.already_in_waitlist')}
          variant="positive"
        />
      )}

      <Text size="700" color="black" weight="bold">
        {content?.title || ''}
      </Text>
      {content?.description && (
        <Text size="400" className="mb-4">
          {content?.description || ''}
        </Text>
      )}
      {content?.buttonLabel && !alreadyInOneWaitlist && (
        <OneLandingButton
          buttonLabel={content?.buttonLabel}
          variant="primary"
          billingAddon={billingAddon}
          moduleName={moduleName}
          componentName="header"
        />
      )}

      <Box
        paddingTop="s48"
        paddingBottom="s80"
        borderRadius={{ all: 'abs016' }}
        className="max-h-[450px] w-[800px]"
        position="relative"
      >
        {ellipsisConfig.map((config, index) => (
          <BlurEllipsis key={index} {...config} />
        ))}
        <GradientMediaContainer maxWidth="800px">
          {videoId ? (
            <iframe
              width="793px"
              height="446px"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`}
              allowFullScreen
              style={{
                borderRadius: '6px 6px 14px 14px',
              }}
            />
          ) : (
            <video
              src={content?.media?.video?.mp4Url || content?.media?.url || ''}
              className="rounded-lg max-h-[450px]"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                justifyContent: 'center',
              }}
              autoPlay
              muted
              loop
            />
          )}
        </GradientMediaContainer>
      </Box>
    </Box>
  )
}

export default OneLandingHeader
